import { motion, useReducedMotion } from 'motion/react';

import ScrollableList from 'components/common/ScrollableList';
import FamilyCustomizeItIllustration from 'images/familyHome/family-customize-it-illustration.jpg';
import FamilyMenuBoxOnDoorMat from 'images/familyHome/family-menu-box-on-door-mat.jpg';
import OvenReadyFamilyMeal from 'images/familyHome/oven-ready-family-meal.jpg';
import WomanShoppingOnCouch from 'images/familyHome/woman-shopping-on-couch.jpg';

const PARENT_VARIANT = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const createSlideInVariants = prefersReducedMotion => ({
  hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 100 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      type: prefersReducedMotion ? 'tween' : 'spring',
    },
  },
});

const HowItWorksStep = ({ imageSrc, imageAlt, stepTitle, stepDescription }) => {
  const prefersReducedMotion = useReducedMotion();
  const slideInVariants = createSlideInVariants(prefersReducedMotion);

  return (
    <motion.li
      variants={slideInVariants}
      className="flex w-72 shrink-0 flex-col items-stretch first:ml-4 last:mr-4"
    >
      <img
        src={imageSrc}
        alt={imageAlt}
        className="mb-6 aspect-square w-full rounded-xl object-cover"
      />
      <div className="mx-auto flex flex-col items-center px-4">
        <h2 className="h5 pb-4 text-center text-green-140">{stepTitle}</h2>
        <p className="text text-center text-green-140">{stepDescription}</p>
      </div>
    </motion.li>
  );
};

HowItWorksStep.propTypes = {
  imageSrc: PropTypes.string.isRequired,
  imageAlt: PropTypes.string.isRequired,
  stepTitle: PropTypes.string.isRequired,
  stepDescription: PropTypes.string.isRequired,
};

const IMAGE_MAP = {
  order_from_couch: WomanShoppingOnCouch,
  customize_it: FamilyCustomizeItIllustration,
  delivered_to_your_doorstep: FamilyMenuBoxOnDoorMat,
  cook_and_enjoy: OvenReadyFamilyMeal,
};

const HowItWorks = ({ data: { title, steps: stepsData } }) => {
  const prefersReducedMotion = useReducedMotion();

  const steps = stepsData.map(step => {
    return {
      ...step,
      imageSrc: IMAGE_MAP[step.key],
    };
  });

  return (
    <section className="-mt-px justify-center bg-family-beige-100 py-8 bp2:py-20">
      <motion.h2
        className="h1 mb-8 text-center text-green-140 bp1:mb-12"
        initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, type: 'spring' }}
        viewport={{ once: true, amount: 0.5 }}
      >
        {title}
      </motion.h2>
      <motion.div
        variants={PARENT_VARIANT}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.5 }}
        className="mx-auto max-w-7xl"
      >
        <ScrollableList
          numSlides={steps.length}
          showArrows={true}
          buttonStyles="invisible bp1:visible shadow-xl bg-white-110 border-2 border-solid border-green-140 hover:bg-white-110 hover:brightness-90 mx-4 top-1/2 -translate-y-1/2"
          iconStyles="iconLg text-green-140"
          slidesContainerStyles="gap-6"
        >
          {steps.map((step, index) => (
            <HowItWorksStep key={index} {...step} />
          ))}
        </ScrollableList>
      </motion.div>
    </section>
  );
};

HowItWorks.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string.isRequired,
    steps: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        stepTitle: PropTypes.string.isRequired,
        stepDescription: PropTypes.string.isRequired,
        imageAlt: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
};

export default HowItWorks;
